import Choo from "choo";
import html from "choo/html";
import Nanobus from "nanobus";

import * as QuizJSON from "./assets/quiz.json";

import { Story, InkList } from "inkjs/engine/Story";
import { Choice } from "inkjs/engine/Choice";

import "./style.scss";

const assertUnreachable = (_: never, s: string): void => {
  throw new Error(s as string);
};

const app = new Choo();
if (process.env.NODE_ENV !== "production") {
  app.use(require("choo-devtools")());
}

type Chunk = {
  type: "story";
  text: string;
};

type AppState = Pick<
  Choo.IState,
  "events" | "params" | "query" | "href" | "route" | "title"
> & {
  story: Story;
  textChunks: Array<Chunk>;
  loaded: boolean;
};

const renderHints = ["image", "intrusion", "an"] as const;
type HintType = typeof renderHints[number] | "plain-text";
type Hint = {
  type: HintType;
  text: string;
};

const seperateRenderHints = (s: string): string[] => {
  const result = [];
  while (s.length > 0) {
    console.log(s);
    const res = s.match(/!\w+?\(.*?\)/);
    if (res === null) {
      result.push(s);
      break;
    } else {
      console.log(res[0], res.index);
      if (res.index === undefined) {
        throw new Error(`undefined hint match index:`);
      }
      if (res.index > 0) {
        result.push(s.slice(0, res.index - 1));
      }
      result.push(res[0]);
      s = s.slice(res.index + res[0].length, s.length - 1);
    }
  }
  return result;
};

const parseRenderHints = (s: string): Hint[] => {
  const parts = seperateRenderHints(s);
  console.log("parts=", parts);

  const r = parts.map(
    (part): Hint => {
      if (part.startsWith("!")) {
        const m = part.match(/!(\w+)\((.*)\)/);
        if (m === null) {
          throw new Error(`couldn't match hint on ${part}`);
        }
        const [_, tag, text] = m;
        if (tag === "image" || tag === "an" || tag === "intrusion") {
          return {
            type: tag,
            text
          };
        } else {
          throw new Error(`unknown hint tag ${tag}`);
        }
      } else {
        return {
          type: "plain-text",
          text: part
        };
      }
    }
  );

  console.log(r);
  return r;
};

const stringHash = (s: string): number => {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    let chr = s.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

const randomSeedClasses = ["rand-0", "rand-1", "rand-2", "rand-3", "rand-4"];

const RenderWithHints = (text: string): HTMLElement[] => {
  const randSeedClass = randomSeedClasses[stringHash(text) % randomSeedClasses.length];

  return parseRenderHints(text).map(hint => {
    if (hint.type === "plain-text") {
      return html`
        <span class="plain-text ${randSeedClass}">${hint.text}</span>
      `;
    } else if (hint.type === "an") {
      return html`
        <span class="author-note ${randSeedClass}">${hint.text}</span>
      `;
    } else if (hint.type === "intrusion") {
      return html`
        <span class="intrusion ${randSeedClass}">${hint.text}</span>
      `;
    } else if (hint.type === "image") {
      return html`
        <img class="image ${randSeedClass}" src="assets/${hint.text}.png" alt=${hint.text} />
      `;
    } else {
      throw assertUnreachable(
        hint.type,
        `unknown hint type: ${hint.type}: ${hint.text}`
      );
    }
  });
};

const RenderChoices = (
  choices: Choice[],
  onClick: (e: MouseEvent, choice: Choice) => void
): HTMLElement => {
  const annotatedChoices = choices.map(choice => {
    const renderHintTypes = new Set(
      parseRenderHints(choice.text).map(hint => hint.type)
    );
    const choiceType =
      renderHintTypes.size === 1
        ? Array.from(renderHintTypes)[0]
        : "plain-text";

    return {
      choiceType,
      choice
    };
  });

  const choicesTypes = new Set(annotatedChoices.map(c => c.choiceType));
  const allChoicesType =
    choicesTypes.size === 1 ? Array.from(choicesTypes)[0] : null;

  return html`
    <section
      id="active-choices"
      class=${allChoicesType !== null ? "active-choices-" + allChoicesType : ""}
    >
      ${annotatedChoices.map(
        ({ choice, choiceType }) => html`
          <div
            class="active-choice active-choice-${choiceType}"
            onclick=${(e: MouseEvent) => onClick(e, choice)}
          >
            ${RenderWithHints(choice.text)}
          </div>
        `
      )}
    </section>
  `;
};

// story management
app.use((state: AppState, emitter) => {
  emitter.on("continue-story", () => {
    if (!state.story.canContinue) {
      return;
    }

    while (state.story.canContinue) {
      const text = state.story.Continue();
      if (text !== null) {
        console.log(text);
        state.textChunks.push({
          type: "story",
          text
        });
      }
    }

    emitter.emit("render");
  });

  emitter.on("choose-choice", choice => {
    console.log(choice);
    state.textChunks = [];
    state.story.ChooseChoiceIndex(choice.index);
    window.scrollTo(0, 0);
    emitter.emit("continue-story");
  });
});

// story loading
app.use((state, emitter) => {
  state.story = new Story(QuizJSON);
  state.loaded = true;
  state.textChunks = [];
  state.currentChoices = [];
  emitter.emit("continue-story");
});

app.route("/*", (state: AppState, emit) => {
  if (state.loaded) {
    return html`
      <div id="root">
        <h1>Would you die of a tragic illness?</h1>
        ${state.textChunks.map(
          (chunk, idx) =>
            html`
              <div id="chunk-${idx}" class="chunk">
                ${RenderWithHints(chunk.text)}
              </div>
            `
        )}
        ${RenderChoices(
          state.story.currentChoices,
          (e: MouseEvent, choice: Choice) => {
            e.preventDefault();
            emit("choose-choice", choice);
          }
        )}
      </div>
    `;
  } else {
    return html`
      <div id="root">
        <h1>Loading...</h1>
      </div>
    `;
  }
});


module.exports = app.mount("#root");
